import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { expertsData } from "../data/experts";

export function useExperts(options = {}) {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchExperts = useCallback(async () => {
    setLoading(true);
    setError(null);

    // 1. Попытка загрузки через наш Express / Prisma бэкенд
    try {
      const queryParams = new URLSearchParams();
      if (options.specialization && options.specialization !== "Все") {
        queryParams.append("specialization", options.specialization);
      }
      if (options.search) {
        queryParams.append("search", options.search);
      }
      if (options.tier) {
        queryParams.append("tier", options.tier);
      }
      if (options.sortBy) {
        queryParams.append("sortBy", options.sortBy);
      }

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
      const res = await fetch(`/api/experts${queryString}`);

      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.experts) && data.experts.length > 0) {
          const mappedFromApi = data.experts.map((e) => {
            const exp = e.expertProfile || {};
            return {
              id: e.id,
              name: e.name || "Эксперт",
              avatar: e.avatarUrl || e.avatar || "",
              title: e.position || exp.title || "Бизнес-эксперт",
              type: "expert",
              specializations: exp.specializations || [],
              experience: exp.experienceYears || 5,
              rating: exp.rating || 5.0,
              hourlyRate: exp.hourlyRate || 5000,
              bio: e.bio || exp.bio || "",
              companies: exp.companies || (e.company ? [{ name: e.company, position: e.position || "" }] : []),
              achievements: exp.achievements || [],
              available: true,
              reviewsCount: exp.reviewsCount || (e.reviews ? e.reviews.length : 0),
              expertTier: exp.tier ? exp.tier.toLowerCase() : "pro",
              reviews: e.reviews || [],
            };
          });

          setExperts(mappedFromApi);
          setLoading(false);
          return;
        }
      }
    } catch (apiErr) {
      console.warn("[useExperts] /api/experts request failed, trying fallback:", apiErr);
    }

    // 2. Fallback через Supabase
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, name, email, role, avatar_url, title, bio, hourly_rate, specializations, companies, achievements, available, type, experience, rating, expert_tier")
        .eq("role", "expert")
        .order("rating", { ascending: false });

      if (profilesError || !profiles || profiles.length === 0) {
        setExperts(mapStaticExperts());
        setLoading(false);
        return;
      }

      const expertIds = (profiles || []).map((p) => p.id);

      let reviewCounts = {};
      let reviewRatings = {};
      if (expertIds.length > 0) {
        const { data: reviewsData } = await supabase
          .from("reviews")
          .select("expert_id, rating")
          .in("expert_id", expertIds);

        if (reviewsData) {
          reviewsData.forEach((r) => {
            reviewCounts[r.expert_id] = (reviewCounts[r.expert_id] || 0) + 1;
            if (!reviewRatings[r.expert_id]) {
              reviewRatings[r.expert_id] = [];
            }
            reviewRatings[r.expert_id].push(r.rating);
          });
        }
      }

      const mapped = (profiles || []).map((p) => ({
        id: p.id,
        name: p.name || "Эксперт",
        avatar: p.avatar_url || "",
        title: p.title || "",
        type: p.type || "expert",
        specializations: p.specializations || [],
        experience: p.experience || 0,
        rating: reviewRatings[p.id] && reviewRatings[p.id].length > 0
          ? Number((reviewRatings[p.id].reduce((s, v) => s + v, 0) / reviewRatings[p.id].length).toFixed(1))
          : 5.0,
        hourlyRate: p.hourly_rate || 5000,
        bio: p.bio || "",
        companies: p.companies || [],
        achievements: p.achievements || [],
        available: p.available !== undefined ? p.available : true,
        reviewsCount: reviewCounts[p.id] || 0,
        expertTier: p.expert_tier || "pro",
        reviews: [],
      }));

      setExperts(mapped);
    } catch (err) {
      console.warn("Network error fetching experts, using static fallback:", err.message);
      setExperts(mapStaticExperts());
    } finally {
      setLoading(false);
    }
  }, [options.specialization, options.search, options.tier, options.sortBy]);

  useEffect(() => {
    fetchExperts();
  }, [fetchExperts]);

  return { experts, loading, error, refetch: fetchExperts };
}

function mapStaticExperts() {
  return expertsData.map((e) => ({
    id: e.id,
    name: e.name,
    avatar: e.avatar,
    title: e.title,
    type: e.type || "expert",
    specializations: e.specializations || [],
    experience: e.experience || 0,
    rating: e.rating || 5.0,
    hourlyRate: e.hourlyRate || 5000,
    bio: e.bio || "",
    companies: e.companies || [],
    achievements: e.achievements || [],
    available: e.available !== undefined ? e.available : true,
    reviewsCount: e.reviews?.length || 0,
    expertTier: (e.expertTier || "pro").toLowerCase(),
    reviews: e.reviews || [],
  }));
}

export function useExpert(id) {
  const [expert, setExpert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || id === "preview") {
      setLoading(false);
      return;
    }

    let mounted = true;

    const load = async () => {
      setLoading(true);

      // 1. Загрузка через наш Express / Prisma эндпоинт
      try {
        const res = await fetch(`/api/experts/${id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.expert && mounted) {
            const exp = data.expert.expertProfile || {};
            setExpert({
              id: data.expert.id,
              name: data.expert.name || "Эксперт",
              avatar: data.expert.avatarUrl || data.expert.avatar || "",
              title: data.expert.position || exp.title || "Бизнес-эксперт",
              type: "expert",
              specializations: exp.specializations || [],
              experience: exp.experienceYears || 5,
              rating: exp.rating || 5.0,
              hourlyRate: exp.hourlyRate || 5000,
              bio: data.expert.bio || exp.bio || "",
              companies: exp.companies || (data.expert.company ? [{ name: data.expert.company, position: data.expert.position || "" }] : []),
              achievements: exp.achievements || [],
              available: true,
              reviewsCount: exp.reviewsCount || (data.expert.reviewsReceived ? data.expert.reviewsReceived.length : 0),
              expertTier: exp.tier ? exp.tier.toLowerCase() : "pro",
              reviews: data.expert.reviewsReceived || [],
              articles: data.expert.articles || [],
            });
            setLoading(false);
            return;
          }
        }
      } catch (apiErr) {
        console.warn("[useExpert] /api/experts/:id failed, trying fallback:", apiErr);
      }

      // 2. Fallback через Supabase
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, name, email, role, avatar_url, title, bio, hourly_rate, specializations, companies, achievements, available, type, experience, rating, expert_tier")
          .eq("id", id)
          .eq("role", "expert")
          .single();

        if (error || !data) {
          const fallback = expertsData.find((e) => e.id === id);
          if (mounted) {
            setExpert(fallback ? {
              id: fallback.id,
              name: fallback.name,
              avatar: fallback.avatar,
              title: fallback.title,
              type: fallback.type || "expert",
              specializations: fallback.specializations || [],
              experience: fallback.experience || 0,
              rating: fallback.rating || 5.0,
              hourlyRate: fallback.hourlyRate || 5000,
              bio: fallback.bio || "",
              companies: fallback.companies || [],
              achievements: fallback.achievements || [],
              available: fallback.available !== undefined ? fallback.available : true,
              reviewsCount: fallback.reviews?.length || 0,
              expertTier: (fallback.expertTier || "pro").toLowerCase(),
              reviews: fallback.reviews || [],
            } : null);
            setLoading(false);
          }
          return;
        }

        const { data: reviewsData } = await supabase
          .from("reviews")
          .select("expert_id, rating")
          .eq("expert_id", id);

        const reviewsCount = reviewsData?.length || 0;
        const avgRating = reviewsData && reviewsData.length > 0
          ? Number((reviewsData.reduce((s, r) => s + r.rating, 0) / reviewsData.length).toFixed(1))
          : 5.0;

        if (mounted) {
          setExpert({
            id: data.id,
            name: data.name || "Эксперт",
            avatar: data.avatar_url || "",
            title: data.title || "",
            type: data.type || "expert",
            specializations: data.specializations || [],
            experience: data.experience || 0,
            rating: avgRating,
            hourlyRate: data.hourly_rate || 5000,
            bio: data.bio || "",
            companies: data.companies || [],
            achievements: data.achievements || [],
            available: data.available !== undefined ? data.available : true,
            reviewsCount,
            expertTier: data.expert_tier || "pro",
            reviews: [],
          });
        }
      } catch (err) {
        console.warn("Network error fetching expert, trying fallback:", err.message);
        const fallback = expertsData.find((e) => e.id === id);
        if (mounted) {
          setExpert(fallback ? {
            id: fallback.id,
            name: fallback.name,
            avatar: fallback.avatar,
            title: fallback.title,
            type: fallback.type || "expert",
            specializations: fallback.specializations || [],
            experience: fallback.experience || 0,
            rating: fallback.rating || 5.0,
            hourlyRate: fallback.hourlyRate || 5000,
            bio: fallback.bio || "",
            companies: fallback.companies || [],
            achievements: fallback.achievements || [],
            available: fallback.available !== undefined ? fallback.available : true,
            reviewsCount: fallback.reviews?.length || 0,
            expertTier: (fallback.expertTier || "pro").toLowerCase(),
            reviews: fallback.reviews || [],
          } : null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [id]);

  return { expert, loading };
}

